import { createSelector, unwrapResult } from '@reduxjs/toolkit'
import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import {
  accountLookup,
  check2faDob,
  CheckoutAppSelector,
  confirmRegistration,
  confirmRegistrationEmail,
  confirmRegistrationMobile,
  finaliseAccountDetails,
  getConsumerName,
  RegistrationState,
  useAppDispatch,
  useAppSelector,
  useConsumerSelector,
  useActiveTags,
  useSMSEnabled,
  verifyRegistrationEmail,
  verifyRegistrationMobile,
  verifyRegistrationMobileSMSEvent,
  verifyRegistrationSMSAuthorization,
  setCohort,
  useCohort,
} from '../'
import { Route } from '../../config/router'
import {
  CashUserMissingData,
  RegistrationData,
  RegistrationVerificationResponse,
  VerificationError,
  VerificationErrorCode,
  VerificationFlow,
  VerificationFlowContext,
  VerificationFlowRoute,
  VerificationMethod,
  VerifyRegistrationMobileEventRequest,
  VerifyRegistrationSMSAuthorizationResponse,
  WelcomeWithInitialLimitStatus,
  NEW_USER_PENDING_HARD_CREDIT_CHECK,
  deferPostRegistrationRouteUntilFeatureFlagsReady,
} from '../../model'
import { CHECKOUT_FLOW_TYPE, postCheckoutFlowMessage } from '../../integrations/inappbrowser-messenger'
import { setDataLayer } from '../../integrations/googletagmanager/utils'
import { GTM_EVENTS_NAME } from '../../integrations/googletagmanager/constant'
import { Cohort } from '@afterpaytouch/portal-api'
import { useIsCountry } from '../country/hooks'

export const registrationSelector: CheckoutAppSelector<RegistrationState> = (state) => state.registration

// @ts-expect-error: OPERATION BLEED STOPPER
export const registrationDataSelector: CheckoutAppSelector<RegistrationData> = createSelector(registrationSelector, (state) => state.data)

// @ts-expect-error: OPERATION BLEED STOPPER
export const cashUserMissingDataSelector: CheckoutAppSelector<CashUserMissingData> = createSelector(registrationSelector, (state) => state.cashUserMissingData)

export const isEmailVerificationRequiredSelector: CheckoutAppSelector<boolean> = createSelector(
  registrationSelector,
  (state) => state.riskRegistrationStatus.emailVerificationRequired
)

export const isEmailVerifiedSelector: CheckoutAppSelector<boolean> = createSelector(
  registrationSelector,
  (state) => state.riskRegistrationStatus.isEmailVerified
)

export const registrationDataNameSelector: CheckoutAppSelector<string> = createSelector(registrationDataSelector, (state) =>
  getConsumerName({ surname: state?.surname, givenNames: state?.givenNames, confirmed: false })
)

export const welcomeWithInitialLimitStatusSelector: CheckoutAppSelector<WelcomeWithInitialLimitStatus> = createSelector(
  registrationSelector,
  (state) => state.welcomeWithInitialLimitStatus
)

export const useRegistration = (): RegistrationState => useAppSelector(registrationSelector)

export const useWelcomeWithInitialLimitStatus = (): WelcomeWithInitialLimitStatus => useAppSelector(welcomeWithInitialLimitStatusSelector)

export const useShouldShowWelcomeWithInitialLimit = (): boolean => {
  const welcomeWithInitialLimitStatus = useWelcomeWithInitialLimitStatus()
  const activeTags = useActiveTags()
  const isGbCountry = useIsCountry(Country.GB)

  return isGbCountry && welcomeWithInitialLimitStatus !== WelcomeWithInitialLimitStatus.Seen && activeTags.includes(NEW_USER_PENDING_HARD_CREDIT_CHECK)
}

export const useRegistrationData = (): RegistrationData => useAppSelector(registrationDataSelector)

export const useRegistrationDataName = (): string => useAppSelector(registrationDataNameSelector)

export const useCashUserMissingData = (): CashUserMissingData => useAppSelector(cashUserMissingDataSelector)

export const useIsEmailVerificationRequired = (): boolean => useAppSelector(isEmailVerificationRequiredSelector)

export const useIsEmailVerified = (): boolean => useAppSelector(isEmailVerifiedSelector)

export const registrationErrors: Partial<Record<ErrorCode, VerificationErrorCode>> = {
  [ErrorCode.SmsCodeNotFound]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.EmailCodeExpired]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.SmsMessagingNotEnabled]: VerificationErrorCode.MethodNotEnabled,
  [ErrorCode.SmsVerificationCodeGenerationMaximumReached]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.SmsVerificationCodeGenerationNotAllowed]: VerificationErrorCode.MethodNotEnabled,
  [ErrorCode.EmailVerificationCodeGenerationNotAllowed]: VerificationErrorCode.MethodNotEnabled,
  [ErrorCode.SmsVerificationCodeValidationMaximumReached]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.InvalidSmsCode]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.InvalidEmailCode]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.MaxSmsCodeAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.MaxEmailCodeAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.NotFound]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.MaxAttemptsReached]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.ExistingPhoneNumber]: VerificationErrorCode.ExistingPhoneNumber,
  [ErrorCode.ExistingPhoneNumberUnicorn]: VerificationErrorCode.ExistingPhoneNumberUnicorn,
  [ErrorCode.ExistingPhoneNumberSendEmail]: VerificationErrorCode.ExistingPhoneNumberSendEmail,
  [ErrorCode.VerificationCodeExpired]: VerificationErrorCode.CodeExpired,
  [ErrorCode.IneligibleState]: VerificationErrorCode.InvalidAddress,
  [ErrorCode.InvalidAddress]: VerificationErrorCode.InvalidAddress,
  [ErrorCode.AddressStateIneligible]: VerificationErrorCode.InvalidAddress,
  [ErrorCode.AddressPoBoxNotAllowed]: VerificationErrorCode.InvalidAddress,
  [ErrorCode.InvalidObject]: VerificationErrorCode.InvalidAddress,
  [ErrorCode.DuplicateIdentity]: VerificationErrorCode.DuplicateIdentity,
  [ErrorCode.HighRiskNationality]: VerificationErrorCode.HighRiskNationality,
  [ErrorCode.IdentityCheckRejected]: VerificationErrorCode.IdentityCheckRejected,
  [ErrorCode.RegistrationNotPermitted]: VerificationErrorCode.RegistrationNotPermitted,
  [ErrorCode.Error]: VerificationErrorCode.Unknown,
  [ErrorCode.InvalidPhoneNumber]: VerificationErrorCode.InvalidPhoneNumber,
  [ErrorCode.AccountCreationFailed]: VerificationErrorCode.AccountCreationFailed,
}

export const useRiskEmailVerificationFlow = (): VerificationFlow => {
  const dispatch = useAppDispatch()
  const { autoSendCode } = useRegistration()
  const isEmailVerified = useIsEmailVerified()
  const emailVerification = (): VerificationFlow => {
    const sendCode = async (): Promise<void> => {
      try {
        unwrapResult(await dispatch(verifyRegistrationEmail()))
      } catch (e) {
        const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
        throw new VerificationError(registrationErrors[errorCode])
      }
    }

    const checkCode = async (code: string): Promise<VerificationFlowRoute> => {
      try {
        unwrapResult(await dispatch(confirmRegistrationEmail(code)))
        return {
          route: Route.SIGN_UP,
        }
      } catch (e) {
        const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
        throw new VerificationError(registrationErrors[errorCode])
      }
    }

    return {
      allowLogout: false,
      autoSendCode,
      checkCode,
      context: VerificationFlowContext.Registration,
      enabled: {
        SMS: false,
        EMAIL: true,
      },
      previousRoute: Route.ENTER_EMAIL,
      prefetchRoutes: [Route.SIGN_UP],
      revealMobile: true,
      sendCode,
    }
  }

  const mobileVerification = (): VerificationFlow => {
    const sendCode = async (): Promise<void> => {
      try {
        unwrapResult(await dispatch(verifyRegistrationMobile()))
      } catch (e) {
        throw convertErrorToVerificationError(e)
      }
    }

    const sendSmsCodeEvent = async (request: VerifyRegistrationMobileEventRequest): Promise<void> => {
      try {
        dispatch(verifyRegistrationMobileSMSEvent(request))
      } catch (e) {
        throw convertErrorToVerificationError(e)
      }
    }

    const sendSmsAuthorization = async (): Promise<VerifyRegistrationSMSAuthorizationResponse> => {
      return unwrapResult(await dispatch(verifyRegistrationSMSAuthorization()))
    }

    const checkCode = async (code: string): Promise<VerificationFlowRoute> => {
      try {
        unwrapResult(await dispatch(confirmRegistrationMobile(code)))
        unwrapResult(await dispatch(confirmRegistration()))
        unwrapResult(await dispatch(finaliseAccountDetails()))

        postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_SIGNUP_COMPLETE)

        setDataLayer(GTM_EVENTS_NAME.ON_SIGNUP_COMPLETE)

        const account = unwrapResult(await dispatch(accountLookup()))
        return deferPostRegistrationRouteUntilFeatureFlagsReady(account.preferredLocale)
      } catch (e) {
        const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
        throw new VerificationError(registrationErrors[errorCode])
      }
    }

    return {
      allowLogout: false,
      autoSendCode,
      checkCode,
      context: VerificationFlowContext.Registration,
      enabled: {
        SMS: true,
        EMAIL: false,
      },
      previousRoute: Route.SIGN_UP,
      prefetchRoutes: [Route.SIGN_UP, Route.LOGGED_IN],
      revealMobile: false,
      sendCode,
      sendSmsCodeEvent,
      sendSmsAuthorization,
    }
  }

  if (!isEmailVerified) {
    return emailVerification()
  } else {
    return mobileVerification()
  }
}

export const useRegistrationFlow = (): VerificationFlow => {
  const isEmailVerificationRequired = useIsEmailVerificationRequired()
  const riskEmailVerificationFlow = useRiskEmailVerificationFlow()
  const newRegistrationFlow = useNewRegistrationFlow()

  return isEmailVerificationRequired ? riskEmailVerificationFlow : newRegistrationFlow
}

export const useNewRegistrationFlow = (): VerificationFlow => {
  const dispatch = useAppDispatch()
  const { autoSendCode } = useRegistration()
  const { mobile } = useConsumerSelector()
  const cohort = useCohort()

  const sendCode = async (method: VerificationMethod = VerificationMethod.SMS): Promise<RegistrationVerificationResponse> => {
    try {
      const smsVerificationResponse = unwrapResult(await dispatch(verifyRegistrationMobile()))
      if (method === VerificationMethod.EMAIL) {
        unwrapResult(await dispatch(verifyRegistrationEmail()))
      }

      return smsVerificationResponse
    } catch (e) {
      throw convertErrorToVerificationError(e)
    }
  }

  const sendSmsCodeEvent = async (request: VerifyRegistrationMobileEventRequest): Promise<void> => {
    try {
      dispatch(verifyRegistrationMobileSMSEvent(request))
    } catch (e) {
      throw convertErrorToVerificationError(e)
    }
  }

  const sendSmsAuthorization = async (): Promise<VerifyRegistrationSMSAuthorizationResponse> => {
    return unwrapResult(await dispatch(verifyRegistrationSMSAuthorization()))
  }

  const checkCode = async (code: string, method: VerificationMethod = VerificationMethod.SMS): Promise<VerificationFlowRoute> => {
    try {
      unwrapResult(await dispatch(method === VerificationMethod.EMAIL ? confirmRegistrationEmail(code) : confirmRegistrationMobile(code)))

      unwrapResult(await dispatch(confirmRegistration()))
      unwrapResult(await dispatch(finaliseAccountDetails()))

      postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_SIGNUP_COMPLETE)

      setDataLayer(GTM_EVENTS_NAME.ON_SIGNUP_COMPLETE)

      if (cohort === Cohort.CashOnly) {
        await dispatch(setCohort(Cohort.NetNew))
      }

      const account = unwrapResult(await dispatch(accountLookup()))
      return deferPostRegistrationRouteUntilFeatureFlagsReady(account.preferredLocale)
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(registrationErrors[errorCode])
    }
  }

  const checkDob = async (dateOfBirth: Date, lastName: string): Promise<void> => {
    try {
      unwrapResult(
        await dispatch(
          check2faDob({
            // @ts-expect-error: OPERATION BLEED STOPPER
            mobile,
            dateOfBirth,
            lastName,
          })
        )
      )
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      throw new VerificationError(registrationErrors[errorCode])
    }
  }

  const smsEnabled = useSMSEnabled()
  if (smsEnabled) {
    return {
      sendCode,
      checkCode,
      autoSendCode,
      context: VerificationFlowContext.Registration,
      allowLogout: false,
      enabled: {
        SMS: true,
        EMAIL: false,
      },
      initialMethod: VerificationMethod.SMS,
      revealMobile: true,
      previousRoute: Route.SIGN_UP,
      prefetchRoutes: [Route.SIGN_UP, Route.LOGGED_IN],
      checkDob,
      sendSmsCodeEvent,
      sendSmsAuthorization,
    }
  } else {
    return {
      sendCode,
      checkCode,
      autoSendCode: true,
      context: VerificationFlowContext.Registration,
      allowLogout: false,
      enabled: {
        SMS: false,
        EMAIL: true,
      },
      initialMethod: VerificationMethod.EMAIL,
      revealMobile: false,
      previousRoute: Route.SIGN_UP,
      prefetchRoutes: [Route.SIGN_UP, Route.LOGGED_IN],
      checkDob,
    }
  }
}

export const convertErrorToVerificationError = (e): VerificationError => {
  const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error

  return new VerificationError(registrationErrors[errorCode])
}
